import { Box, Group, Stack, Text } from '@mantine/core';
import { Microscope } from 'lucide-react';
import type { ReactElement } from 'react';
import { SurfaceCard } from '@/design/SurfaceCard';
import type { TopicDefinition } from './TopicCatalogTypes';
import type { InteractionRecord } from '@/shared/analytics/AnalyticsTypes';
import { formatCount, formatPercent } from '@/shared/analytics/MetricFormatting';

interface ProvenancePanelProps {
    definition: TopicDefinition;
    records: InteractionRecord[];
    /** Show only the confidence section, dropping signal source and classifier details. */
    confidenceOnly?: boolean;
}

interface ConfidenceStats {
    min: number;
    avg: number;
    max: number;
    count: number;
}

function confidenceStats(values: number[]): ConfidenceStats | null {
    if (values.length === 0) return null;
    let min = 1;
    let max = 0;
    let sum = 0;
    for (const value of values) {
        if (value < min) min = value;
        if (value > max) max = value;
        sum += value;
    }
    return { min, max, avg: sum / values.length, count: values.length };
}

function shortClassifierState(id: string): string {
    const parts = id.split(':');
    return parts.slice(-3).join(':');
}

export default function ProvenancePanel({ definition, records, confidenceOnly = false }: ProvenancePanelProps): ReactElement {
    // Chrome speaks brand — the confidence widget is no longer tinted by the screen's (retired) accent.
    const tokens = {
        solid: 'var(--bm-color-brand-accent)',
        soft: 'var(--bm-color-brand-soft)',
        border: 'var(--bm-color-brand-border)',
        text: 'var(--bm-color-brand-accent)',
    };
    const field = definition.primaryField;
    const isDimension = records.some((record) => record.dimensions[field] !== undefined);

    const methodCounts = new Map<string, number>();
    const fieldConfidences: number[] = [];
    const overallConfidences: number[] = [];
    const classifierStates = new Set<string>();

    for (const record of records) {
        if (record.classifierStateId !== null) classifierStates.add(record.classifierStateId);
        const dim = record.dimensions[field];
        if (dim !== undefined) {
            methodCounts.set(dim.method, (methodCounts.get(dim.method) ?? 0) + 1);
            if (dim.confidence !== null) fieldConfidences.push(dim.confidence);
        }
        for (const value of Object.values(record.dimensions)) {
            if (value.confidence !== null) overallConfidences.push(value.confidence);
        }
    }

    const stats = confidenceStats(isDimension ? fieldConfidences : overallConfidences);
    const methods = [...methodCounts.entries()].sort((a, b) => b[1] - a[1]);
    const states = [...classifierStates];

    return (
        <SurfaceCard $padding="md">
            <Group gap="sm" mb="md">
                <Box
                    style={{
                        width: 26,
                        height: 26,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: tokens.soft,
                        border: `1px solid ${tokens.border}`,
                        borderRadius: 'var(--bm-radius)',
                    }}
                >
                    <Microscope size={14} color={tokens.solid} />
                </Box>
                <Box>
                    <Text size="sm" fw={700} style={{ letterSpacing: '-0.02em' }}>
                        {confidenceOnly ? 'Classification Confidence' : 'How we know'}
                    </Text>
                    {!confidenceOnly && (
                        <Text size="xs" c="dimmed" style={{ fontSize: '10px' }}>
                            Provenance behind this slice
                        </Text>
                    )}
                </Box>
            </Group>

            <Stack gap="lg">
                {!confidenceOnly && (
                <Box>
                    <Text size="xs" c="dimmed" fw={700} tt="uppercase" mb="xs" style={{ fontSize: '9px', letterSpacing: '0.08em' }}>
                        Signal source
                    </Text>
                    {isDimension ?
                        <Stack gap={6}>
                            {methods.map(([method, count]) => (
                                <Group key={method} justify="space-between" wrap="nowrap">
                                    <Text size="xs" fw={600}>
                                        {method}
                                    </Text>
                                    <Text size="xs" ff="monospace" c="dimmed">
                                        {formatCount(count)} turns
                                    </Text>
                                </Group>
                            ))}
                        </Stack>
                    :   <Text size="xs" c="dimmed" lh={1.5}>
                            Direct attribute — read straight from the record, not classifier-inferred.
                        </Text>
                    }
                </Box>
                )}

                {stats !== null && (
                    <Box>
                        <Group justify="space-between" mb="xs">
                            <Text size="xs" c="dimmed" fw={700} tt="uppercase" style={{ fontSize: '9px', letterSpacing: '0.08em' }}>
                                {isDimension ? 'Field confidence' : 'Overall classification confidence'}
                            </Text>
                            <Text size="xs" ff="monospace" fw={700} style={{ color: tokens.text }}>
                                {formatPercent(stats.avg)} avg
                            </Text>
                        </Group>
                        <Box style={{ position: 'relative', height: 8, background: 'var(--bm-color-surface)' }}>
                            <Box
                                style={{
                                    position: 'absolute',
                                    left: `${stats.min * 100}%`,
                                    width: `${Math.max(0, (stats.max - stats.min) * 100)}%`,
                                    height: '100%',
                                    background: tokens.soft,
                                }}
                            />
                            <Box style={{ position: 'absolute', left: `${stats.avg * 100}%`, width: 2, height: '100%', background: tokens.solid }} />
                        </Box>
                        <Group justify="space-between" mt={4}>
                            <Text c="dimmed" ff="monospace" style={{ fontSize: '9px' }}>
                                min {formatPercent(stats.min)}
                            </Text>
                            <Text c="dimmed" ff="monospace" style={{ fontSize: '9px' }}>
                                max {formatPercent(stats.max)}
                            </Text>
                        </Group>
                    </Box>
                )}

                {!confidenceOnly && states.length > 0 && (
                    <Box>
                        <Text size="xs" c="dimmed" fw={700} tt="uppercase" mb="xs" style={{ fontSize: '9px', letterSpacing: '0.08em' }}>
                            Classifier ({states.length})
                        </Text>
                        <Stack gap={4}>
                            {states.slice(0, 3).map((state) => (
                                <Text key={state} size="xs" ff="monospace" c="dimmed" truncate title={state} style={{ fontSize: '10px' }}>
                                    {shortClassifierState(state)}
                                </Text>
                            ))}
                        </Stack>
                    </Box>
                )}
            </Stack>
        </SurfaceCard>
    );
}
